/**
 * ⭐⭐ **LANÇAR OUTRO ATIVO, PASSO A PASSO — 18/09/2026** (PRD §45).
 *
 * Ação de companhia aberta, marca, patente e direito minerário, no molde da
 * ficha de veículo e de ato (mesmas peças de `PassoAPasso`), num arquivo à
 * parte para a ficha de veículo e ato — que está no ar — não mudar.
 *
 * **A ação** tem os dois eixos que ele escolheu: a situação da POSIÇÃO do
 * investigado (o selo) e a situação da COMPANHIA (um texto, como o cadastro da
 * CVM a escreve). ⚠️ **O valor não se digita:** a pessoa informa quantidade e
 * cotação, e o SERVIDOR multiplica e grava — a tela só mostra a conta.
 *
 * **Marca, patente e direito minerário** têm o registro, as avaliações (a mesma
 * tabela do imóvel) e os gravames. ⛔ Não somam — a regra dele —, e a ficha diz
 * isso na hora de escolher a situação.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  apiInvestigados,
  type AtivoDoInvestigado,
  type ClassificacaoDoAtivo,
  type EspecieDeOutroAtivo,
  type Investigado,
} from '../lib/api';
import { paraTela } from '../lib/datas';
import { avisar, avisarErro } from './Avisos';
import { completarReais, digitarReais } from '../lib/dinheiro.ts';
import { usarFormulario } from '../lib/formulario.ts';
import { GuardaDoFormulario } from './GuardaDoFormulario';
import { Confirmar } from './Confirmar';
import { Icone } from './Icone';
import { usarEhMaster } from '../lib/papel';
import { AjudaDoPasso, CampoDoPasso, PassosDoTopo, Progresso, ResumoDoPasso } from './PassoAPasso';
import {
  dinheiro,
  FormularioDeOnus,
  ROTULO_DA_MODALIDADE,
  TabelaDeAvaliacoes,
  type LinhaDeAvaliacao,
} from './AtivosLocalizados';

type Situacao = { chave: ClassificacaoDoAtivo; curto: string; tom: string; define: string };

/** O que é cada espécie, na ordem dos blocos da lista. */
export const OUTROS: Array<{ chave: EspecieDeOutroAtivo; um: string; varios: string }> = [
  { chave: 'acao', um: 'Ação de companhia aberta', varios: 'Ações de companhia aberta' },
  { chave: 'marca', um: 'Marca', varios: 'Marcas' },
  { chave: 'patente', um: 'Patente', varios: 'Patentes' },
  { chave: 'direito_minerario', um: 'Direito minerário', varios: 'Direitos minerários' },
  /* ⭐ 07/10/2026, pedido da Karen. */
  { chave: 'direito_creditorio', um: 'Direito creditório', varios: 'Direitos creditórios' },
  /* ⭐ 07/10/2026: "Offshore" é o nome da opção NA TELA, a pedido dele. ⛔ No documento continua
     "participação no exterior" (decisão 16, 13/09): offshore lê-se como acusação. */
  { chave: 'participacao_exterior', um: 'Offshore (participação no exterior)', varios: 'Offshores (participações no exterior)' },
];

/** Onde o fato mora, bloco a bloco — tela interna: é o que faz "nada lançado" não parecer "não tem". */
export const FONTE_DO_OUTRO: Record<EspecieDeOutroAtivo, string> = {
  acao:
    'Quem tem 5% ou mais consta do formulário de referência da companhia, na CVM (itens 6.1 e 6.2). ' +
    'Abaixo de 5%, a posição só aparece por ordem judicial ou extrato juntado ao processo.',
  marca: 'Pedidos e registros no INPI, localizados pelo nome do titular.',
  patente: 'Pedidos e patentes no INPI, localizados pelo nome do titular.',
  direito_minerario: 'Cadastro Mineiro da ANM, pelo CPF ou CNPJ do titular.',
  direito_creditorio:
    'Precatórios e requisições de pagamento, processos em que a parte é credora, títulos e contratos a receber, ' +
    'habilitações de crédito em recuperação judicial ou falência.',
  participacao_exterior:
    'Registro societário da jurisdição onde a sociedade foi constituída. A pista vem do botão "No exterior", na faixa desta parte, e só entra aqui conferida.',
};

/**
 * As situações de cada espécie, com o que cada uma faz na conta — as dele
 * (decisão 15 e §45.1). O tom é o da classificação do imóvel.
 */
export const SITUACOES_DO_OUTRO: Record<EspecieDeOutroAtivo, Situacao[]> = {
  acao: [
    { chave: 'acao_em_nome', curto: 'Em nome do investigado', tom: 'livre', define: 'Consta, na data do documento, em nome dele. Soma numa linha própria do cartão de cobertura, fora do patrimônio livre: o ônus de ação não é público.' },
    { chave: 'acao_onerada', curto: 'Onerada', tom: 'onerado', define: 'Penhor, alienação fiduciária, usufruto ou cláusula restritiva. Soma com o onerado, como o imóvel onerado.' },
    { chave: 'acao_com_constricao', curto: 'Com constrição judicial', tom: 'onerado', define: 'Bloqueio, penhora, indisponibilidade ou retenção de proventos. Soma com o onerado.' },
    { chave: 'acao_deixou_de_constar', curto: 'Deixou de constar', tom: 'transmitido', define: 'Constava num documento e não consta no posterior. Não soma — e não afirma venda: a posição pode ter caído abaixo de 5%.' },
  ],
  marca: [
    { chave: 'marca_em_vigor', curto: 'Em vigor', tom: 'livre', define: 'Registro vigente no nome dele. Não soma: fica fora da conta por regra, com o valor documentado na ficha.' },
    { chave: 'marca_pedido', curto: 'Pedido', tom: 'neutro', define: 'Pedido depositado, ainda sem registro. Não soma.' },
    { chave: 'marca_com_onus', curto: 'Com ônus', tom: 'onerado', define: 'Com penhora ou outro ônus anotado no INPI. Não soma; o ônus sai na ficha.' },
    { chave: 'marca_transferida', curto: 'Transferida', tom: 'transmitido', define: 'Esteve no nome dele e foi transferida. Saiu do patrimônio.' },
    { chave: 'marca_extinta', curto: 'Extinta', tom: 'encerrada', define: 'Registro extinto — caducidade, falta de prorrogação ou renúncia. Não é valor zero: é situação.' },
  ],
  patente: [
    { chave: 'patente_em_vigor', curto: 'Em vigor', tom: 'livre', define: 'Patente concedida e vigente no nome dele. Não soma: fica fora da conta por regra.' },
    { chave: 'patente_pedido', curto: 'Pedido', tom: 'neutro', define: 'Pedido depositado, ainda em exame. Não soma.' },
    { chave: 'patente_com_onus', curto: 'Com ônus', tom: 'onerado', define: 'Com penhora ou outro ônus anotado no INPI. Não soma; o ônus sai na ficha.' },
    { chave: 'patente_transferida', curto: 'Transferida', tom: 'transmitido', define: 'Esteve no nome dele e foi transferida. Saiu do patrimônio.' },
    { chave: 'patente_extinta', curto: 'Extinta', tom: 'encerrada', define: 'Extinta — fim do prazo, falta de anuidade ou renúncia. É situação, não valor zero.' },
  ],
  direito_minerario: [
    { chave: 'mineral_sem_onus_averbado', curto: 'Sem ônus averbado identificado', tom: 'livre', define: 'Título no nome dele, sem ônus averbado na ANM até a data da consulta. Não soma: fica fora da conta por regra.' },
    { chave: 'mineral_onerado', curto: 'Onerado', tom: 'onerado', define: 'Com ônus ou garantia averbada na ANM. Não soma; o ônus sai na ficha.' },
    { chave: 'mineral_cedido', curto: 'Cedido', tom: 'transmitido', define: 'Esteve no nome dele e foi cedido. Saiu do patrimônio.' },
    { chave: 'mineral_extinto', curto: 'Extinto', tom: 'encerrada', define: 'Título extinto — caducidade, renúncia ou fim do prazo.' },
  ],
  /* ⭐ 07/10/2026 — as situações dele: o livre soma quando tem valor; o resto fica fora. */
  direito_creditorio: [
    { chave: 'credito_livre', curto: 'Livre', tom: 'livre', define: 'Crédito a receber no nome dele, sem penhora, cessão ou outro ônus identificado. Soma quando há valor documentado.' },
    { chave: 'credito_onerado', curto: 'Penhorado ou onerado', tom: 'onerado', define: 'Com penhora no rosto dos autos, cessão fiduciária, caução ou outro ônus. Continua dele e fica fora da conta; o ônus sai na ficha.' },
    { chave: 'credito_cedido', curto: 'Cedido', tom: 'transmitido', define: 'Esteve no nome dele e foi cedido a outra pessoa. Saiu do patrimônio.' },
    { chave: 'credito_extinto', curto: 'Extinto ou recebido', tom: 'encerrada', define: 'Recebido, pago, compensado ou extinto. Não é valor zero: é situação.' },
  ],
  /* ⭐ 28/09/2026. ⛔ Só entra CONFIRMADA no registro da jurisdição — o servidor recusa sem ele. */
  participacao_exterior: [
    { chave: 'exterior_vigente', curto: 'Confirmada', tom: 'livre', define: 'Participação vigente, conferida no registro da jurisdição. Não soma: fica fora da conta por regra, como a marca.' },
    { chave: 'exterior_transferida', curto: 'Transferida', tom: 'transmitido', define: 'Esteve em nome dele e foi transferida. Saiu do patrimônio.' },
    { chave: 'exterior_encerrada', curto: 'Sociedade encerrada', tom: 'encerrada', define: 'A sociedade foi dissolvida, baixada ou cancelada no registro da jurisdição.' },
  ],
};

const PISTA_DO_OUTRO: Record<EspecieDeOutroAtivo, { bem: string; registro: string; rotulo: string }> = {
  acao: { bem: 'Companhia Exemplo S.A. — ações ordinárias (ON)', registro: 'CNPJ 00.000.000/0001-00 · EXEM3', rotulo: 'A ação' },
  marca: { bem: 'Marca Exemplo — nominativa', registro: 'Processo INPI 900000000 · classe 35', rotulo: 'A marca' },
  patente: { bem: 'Dispositivo de vedação — modelo de utilidade', registro: 'BR 20 2024 000000-0', rotulo: 'A patente' },
  direito_minerario: { bem: 'Concessão de lavra — granito — Cidade/UF', registro: 'Processo ANM 800.000/2015', rotulo: 'O direito minerário' },
  direito_creditorio: { bem: 'Precatório alimentar contra o Estado de São Paulo', registro: 'Precatório 0000000-00.2024.8.26.0500', rotulo: 'O crédito' },
  participacao_exterior: { bem: 'EXEMPLO HOLDINGS LTD', registro: 'Registro nº 1234567', rotulo: 'A sociedade' },
};

const NATUREZAS: Record<EspecieDeOutroAtivo, string[]> = {
  acao: ['Penhor', 'Alienação fiduciária', 'Usufruto', 'Cláusula de inalienabilidade', 'Penhora', 'Bloqueio judicial', 'Indisponibilidade'],
  marca: ['Penhora', 'Penhor', 'Arresto', 'Indisponibilidade', 'Licença exclusiva'],
  patente: ['Penhora', 'Penhor', 'Arresto', 'Indisponibilidade', 'Licença exclusiva'],
  direito_minerario: ['Penhor', 'Hipoteca', 'Penhora', 'Arresto', 'Indisponibilidade'],
  participacao_exterior: ['Penhora', 'Penhor', 'Arresto', 'Indisponibilidade'],
  direito_creditorio: ['Penhora no rosto dos autos', 'Cessão fiduciária', 'Caução', 'Penhora', 'Arresto', 'Indisponibilidade'],
};

/** Que data é a de `ocorridoEm`, pela situação — o mesmo rótulo do documento. */
function rotuloDaData(c: ClassificacaoDoAtivo): string {
  if (c === 'marca_transferida' || c === 'patente_transferida') return 'Data da transferência';
  if (c === 'mineral_cedido') return 'Data da cessão';
  if (c === 'marca_extinta' || c === 'patente_extinta' || c === 'mineral_extinto') return 'Data da extinção';
  if (c === 'mineral_sem_onus_averbado' || c === 'mineral_onerado') return 'Outorga do título';
  if (c === 'exterior_transferida') return 'Data da transferência';
  if (c === 'exterior_encerrada') return 'Data do encerramento';
  if (c === 'exterior_vigente') return 'Ingresso ou constituição';
  if (c === 'credito_cedido') return 'Data da cessão';
  if (c === 'credito_extinto') return 'Data da extinção ou do recebimento';
  if (c === 'credito_livre' || c === 'credito_onerado') return 'Origem do crédito';
  return 'Depósito ou concessão';
}
const SAIU: ClassificacaoDoAtivo[] = ['marca_transferida', 'patente_transferida', 'mineral_cedido', 'exterior_transferida', 'credito_cedido'];

/** O papel na sociedade, como costuma vir escrito. É sugestão: a escrita é livre. */
const QUALIDADES_NO_EXTERIOR = ['Sócio', 'Diretor', 'Beneficiário final', 'Beneficiário', 'Secretário', 'Representante legal', 'Sócio fiduciário (nominee)'];

/** ⭐ O que a ficha recebe já preenchido quando nasce de uma pista (o botão "No exterior"). */
export type SugestaoDeOutroAtivo = {
  outro: EspecieDeOutroAtivo;
  classificacao?: ClassificacaoDoAtivo;
  descricao?: string;
  cidade?: string;
  qualidadeNoAto?: string;
  ocorridoEm?: string;
};

type GravameNaFicha = { natureza: string; credor: string; valor: string; constituidoEm: string; situacao: string };

const AJUDA_DA_ACAO: Array<{ titulo: string; corpo: () => ReactNode }> = [
  {
    titulo: 'Dois eixos',
    corpo: () => (
      <>
        <p>
          A <b>situação</b> é a da posição do investigado. A <b>companhia</b> tem a dela, e vai por extenso, como o cadastro da CVM a escreve.
        </p>
        <p>Nenhuma diz "livre": penhor e bloqueio de ação moram na B3, fora da vista de terceiros.</p>
      </>
    ),
  },
  {
    titulo: 'De quando é a posição',
    corpo: () => (
      <p>
        A posição é a do <b>documento</b> — formulário de referência, comunicação de participação ou extrato juntado ao processo — e vale para a data dele.
      </p>
    ),
  },
  {
    titulo: 'Por que a cotação',
    corpo: () => (
      <p>
        A cotação oficial do dia <b>substitui a avaliação</b> da ação (CPC, art. 871, III). O Hub multiplica pela quantidade; sem uma das duas, o valor fica em branco — nunca zero.
      </p>
    ),
  },
  {
    titulo: 'Gravame',
    corpo: () => (
      <p>
        Penhor, alienação fiduciária, usufruto ou bloqueio. O valor é o do ato, <b>não o saldo devedor</b>, e nunca se subtrai. Só gravame ativo pesa.
      </p>
    ),
  },
];

/** ⭐ 07/10/2026 — a ajuda do direito creditório, que soma quando está livre (decisão dele). */
const AJUDA_DO_CREDITO: Array<{ titulo: string; corpo: () => ReactNode }> = [
  {
    titulo: 'O livre soma',
    corpo: () => (
      <>
        <p>
          O crédito <b>livre</b> entra no valor apurado quando há valor documentado — o valor vezes a participação, como o imóvel livre.
        </p>
        <p>Penhorado ou onerado continua dele e fica fora da conta. Cedido ou extinto saiu do patrimônio.</p>
      </>
    ),
  },
  {
    titulo: 'A origem',
    corpo: () => (
      <p>
        A data do documento que constitui o crédito, ou a da cessão ou extinção. A data da consulta diz <b>de quando é o retrato</b>.
      </p>
    ),
  },
  {
    titulo: 'O valor documentado',
    corpo: () => (
      <p>
        O valor do precatório, do título ou do contrato, como o documento o diz, e <b>sem atualização</b>. Valor do documento não é valor recebido. Sem valor, não é zero.
      </p>
    ),
  },
  {
    titulo: 'Gravame',
    corpo: () => (
      <p>
        Penhora no rosto dos autos, cessão fiduciária ou caução. Com gravame ativo, marque a situação como penhorado ou onerado.
      </p>
    ),
  },
];

const AJUDA_DO_REGISTRO: Array<{ titulo: string; corpo: () => ReactNode }> = [
  {
    titulo: 'Não soma',
    corpo: () => (
      <>
        <p>
          Marca, patente e direito minerário <b>ficam fora da conta</b>, mesmo em vigor: sem cotação, o valor depende de laudo e pode se extinguir com o título, e a venda depende de comprador habilitado.
        </p>
        <p>Transferida, cedida ou extinta saiu do patrimônio.</p>
      </>
    ),
  },
  {
    titulo: 'O registro',
    corpo: () => (
      <p>
        O número e as datas como o INPI ou a ANM os escrevem. A data da consulta diz <b>de quando é o retrato</b>.
      </p>
    ),
  },
  {
    titulo: 'O valor documentado',
    corpo: () => (
      <p>
        Sai na ficha, com a procedência, e <b>não entra na conta</b>. Laudo de integralização é da empresa que recebeu o bem, não do investigado. Sem avaliação, não é zero.
      </p>
    ),
  },
  {
    titulo: 'Gravame',
    corpo: () => (
      <p>
        Penhora, penhor ou outro ônus anotado no INPI ou averbado na ANM. Situação em branco é "não apurada".
      </p>
    ),
  },
];

const AJUDA_DO_EXTERIOR: Array<{ titulo: string; corpo: () => ReactNode }> = [
  {
    titulo: 'Só confirmada',
    corpo: () => (
      <>
        <p>
          A base do ICIJ reúne documentos vazados: é <b>pista, não registro</b>, e não traz CPF nem CNPJ. Lance aqui só o que o <b>registro da jurisdição</b> confirmou.
        </p>
        <p>No documento sai a sociedade e o país. Nunca a palavra "offshore", nunca a base de onde veio a pista.</p>
      </>
    ),
  },
  {
    titulo: 'O registro',
    corpo: () => (
      <p>
        Diga <b>perante quem</b> a participação foi conferida e em que data: é isso que o documento afirma. Sem o registro, o Hub não grava.
      </p>
    ),
  },
  {
    titulo: 'O valor documentado',
    corpo: () => (
      <p>
        Sai na ficha, com a procedência, e <b>não entra na conta</b>: o valor depende de documento da própria sociedade. Sem avaliação, não é zero.
      </p>
    ),
  },
  {
    titulo: 'Gravame',
    corpo: () => <p>Penhora, penhor ou outro ônus anotado no registro da jurisdição. Situação em branco é "não apurada".</p>,
  },
];

export function FichaDeOutroAtivo({
  investigado,
  corrigindo,
  sugestao,
  aoFechar,
  aoGravar,
}: {
  investigado: Investigado;
  corrigindo: AtivoDoInvestigado | null;
  /** ⭐ 28/09/2026: a ficha nova já preenchida, vinda de uma pista. */
  sugestao?: SugestaoDeOutroAtivo | null;
  aoFechar: () => void;
  aoGravar: () => void;
}) {
  const ehMaster = usarEhMaster();
  const inicial = corrigindo;
  const emCampo = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','));
  const emReais = (v: number | null | undefined) =>
    v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const emMilhar = (v: number | null | undefined) => (v === null || v === undefined ? '' : v.toLocaleString('pt-BR'));

  const doSugerido = !corrigindo ? (sugestao ?? null) : null;
  const [outro, setOutro] = useState<EspecieDeOutroAtivo>(inicial?.outro ?? doSugerido?.outro ?? 'acao');
  const [classificacao, setClassificacao] = useState<ClassificacaoDoAtivo>(
    inicial?.classificacao ?? doSugerido?.classificacao ?? (doSugerido ? SITUACOES_DO_OUTRO[doSugerido.outro][0]!.chave : 'acao_em_nome'),
  );
  const [descricao, setDescricao] = useState(inicial?.descricao ?? doSugerido?.descricao ?? '');
  /* ⭐ Da participação no exterior: a jurisdição, a qualidade e o registro que confirmou. */
  const doExterior = inicial?.outro === 'participacao_exterior';
  const [jurisdicao, setJurisdicao] = useState((doExterior ? inicial?.cidade : doSugerido?.cidade) ?? '');
  const [qualidade, setQualidade] = useState((doExterior ? inicial?.qualidadeNoAto : doSugerido?.qualidadeNoAto) ?? '');
  const [registro, setRegistro] = useState((doExterior ? inicial?.cartorio : null) ?? '');
  const [identificacao, setIdentificacao] = useState(inicial?.identificacao ?? '');
  /* ⭐ Do direito creditório (07/10/2026): quem deve à parte. */
  const [devedor, setDevedor] = useState((inicial?.outro === 'direito_creditorio' ? inicial?.outraParte : null) ?? '');
  const [companhia, setCompanhia] = useState(inicial?.outro === 'acao' ? (inicial.situacao ?? '') : '');
  const [quantidade, setQuantidade] = useState(emMilhar(inicial?.quantidade));
  const [capital, setCapital] = useState(emCampo(inicial?.percentualDoCapital));
  const [cotacao, setCotacao] = useState(emReais(inicial?.cotacao));
  const [cotacaoEm, setCotacaoEm] = useState(inicial?.cotacaoEm ?? '');
  const [certidaoEm, setCertidaoEm] = useState(inicial?.certidaoEm ?? '');
  const [percentual, setPercentual] = useState(emCampo(inicial?.outro === 'acao' ? null : inicial?.percentual));
  const [ocorridoEm, setOcorridoEm] = useState(inicial?.ocorridoEm ?? doSugerido?.ocorridoEm ?? '');
  const [prazo, setPrazo] = useState(inicial?.prazoDeValidade ?? '');
  const [adquirente, setAdquirente] = useState(inicial?.adquirente ?? '');
  const [vinculo, setVinculo] = useState(inicial?.vinculoEntrePartes ?? '');
  const [proprietarioAtual, setProprietarioAtual] = useState(inicial?.proprietarioAtual ?? '');
  const [avaliacoes, setAvaliacoes] = useState<LinhaDeAvaliacao[]>(() =>
    inicial?.outro === 'acao'
      ? []
      : (inicial?.avaliacoes ?? []).map((a) => ({
          id: a.id,
          modalidade: a.modalidade ?? '',
          valor: emReais(a.valor),
          referencia: a.referencia ?? '',
          avaliadaEm: a.avaliadaEm ?? '',
          conta: a.entraNaConta,
        })),
  );
  const [gravames, setGravames] = useState<GravameNaFicha[]>([]);
  const [onusExistentes, setOnusExistentes] = useState(corrigindo?.onus ?? []);
  const [gravameAApagar, setGravameAApagar] = useState<AtivoDoInvestigado['onus'][number] | null>(null);

  const ehAcao = outro === 'acao';
  const ehExterior = outro === 'participacao_exterior';
  const ehCredito = outro === 'direito_creditorio';
  const saiu = SAIU.includes(classificacao);
  const deixou = classificacao === 'acao_deixou_de_constar';
  const passos = ehAcao
    ? ['A ação', 'A posição', 'A cotação', 'Gravames', corrigindo ? 'Conferir e gravar' : 'Conferir e lançar']
    : ['O bem', 'O registro', 'Avaliações', 'Gravames', corrigindo ? 'Conferir e gravar' : 'Conferir e lançar'];
  const ULTIMO = passos.length - 1;
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(corrigindo ? ULTIMO : 0);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const emVoo = useRef(false);
  const topo = useRef<HTMLDivElement>(null);

  /* ⚠️ Tudo o que a ficha edita entra na guarda: fechar com algo digitado avisa. */
  const guarda = usarFormulario(
    { outro, classificacao, descricao, identificacao, companhia, quantidade, capital, cotacao, cotacaoEm, certidaoEm, percentual, ocorridoEm, prazo, adquirente, vinculo, proprietarioAtual, avaliacoes, gravames, jurisdicao, qualidade, registro, devedor },
    aoFechar,
  );
  useEffect(() => {
    topo.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [passo]);

  /** Vazio vira nulo, e nunca zero. O ponto de milhar sai antes (a lição de 10/09). */
  const numero = (t: string) => (t.trim() === '' ? null : Number(t.replace(/\./g, '').replace(',', '.')));
  const inteiro = (t: string) => (t.replace(/\D/g, '') === '' ? null : Number(t.replace(/\D/g, '')));
  const linhasDeAvaliacao = avaliacoes.filter((a) => a.valor.trim() !== '' || a.modalidade !== '' || a.referencia.trim() !== '');
  const q = inteiro(quantidade);
  const c = numero(cotacao);
  /* ⚠️ Só para MOSTRAR: quem grava a conta é o servidor (`valoresDaAcao`). */
  const valorDaPosicao = q !== null && c !== null ? Math.round(q * c * 100) / 100 : null;

  useEffect(() => {
    if (erro && !problemaDoPasso(passo)) setErro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descricao, avaliacoes, classificacao, capital, percentual, jurisdicao, registro]);

  function trocarOutro(o: EspecieDeOutroAtivo) {
    setOutro(o);
    setClassificacao(SITUACOES_DO_OUTRO[o][0]!.chave);
    /* A tabela de avaliações não vale na ação (o valor é a cotação), e a
       cotação não vale fora dela: a troca limpa o que deixou de caber. */
    if (o === 'acao') setAvaliacoes([]);
  }

  function problemaDoPasso(p: number): string | null {
    if (p === 0 && descricao.trim().length < 2) return `Descreva ${PISTA_DO_OUTRO[outro].rotulo.toLowerCase()} — é o que sai impresso.`;
    if (p === 0 && ehExterior && jurisdicao.trim().length < 2) return 'Diga em que país ou jurisdição a sociedade está constituída.';
    if (p === 1 && ehExterior && registro.trim().length < 3) return 'Diga em que registro da jurisdição a participação foi conferida. A base do ICIJ é pista, não registro.';
    if (p === 1 && ehAcao && capital.trim() && !(numero(capital)! >= 0 && numero(capital)! <= 100)) return 'O percentual do capital vai de 0 a 100.';
    if (p === 1 && !ehAcao && percentual.trim() && !(numero(percentual)! >= 0 && numero(percentual)! <= 100)) return 'A participação vai de 0 a 100.';
    if (p === 2 && !ehAcao) {
      if (linhasDeAvaliacao.some((a) => numero(a.valor) === null || !(numero(a.valor)! > 0))) return 'Informe o valor de cada avaliação, ou tire a linha.';
      if (linhasDeAvaliacao.some((a) => !a.id && a.modalidade === '')) return 'Diga de onde veio cada avaliação nova.';
    }
    return null;
  }
  function irPara(p: number) {
    setErro(null);
    setPasso(p);
  }
  function avancar() {
    const x = problemaDoPasso(passo);
    if (x) return setErro(x);
    setAlcancado((a) => Math.max(a, passo + 1));
    irPara(passo + 1);
  }
  function pedirParaGravar() {
    if (emVoo.current) return;
    for (let k = 0; k < ULTIMO; k++) {
      const x = problemaDoPasso(k);
      if (x) {
        setPasso(k);
        return setErro(x);
      }
    }
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setGravando(true);
    setErro(null);
    /*
     * ⚠️ **Os campos que esta ficha não mostra vão como vieram** — a mesma
     * regra da ficha de veículo: mandar só os editados deixaria a correção
     * dependente de lembrar cada um.
     */
    const dados = {
      classificacao,
      especie: null,
      descricao,
      identificacao: identificacao.trim() || null,
      situacao: ehAcao ? companhia.trim() || null : (inicial?.outro === 'acao' ? null : (inicial?.situacao ?? null)),
      certidaoEm: certidaoEm || null,
      ...(ehAcao
        ? {
            quantidade: q,
            cotacao: deixou && !cotacao.trim() ? null : c,
            cotacaoEm: cotacaoEm || null,
            percentualDoCapital: numero(capital),
            ocorridoEm: null,
            prazoDeValidade: null,
            adquirente: null,
            vinculoEntrePartes: null,
            proprietarioAtual: null,
          }
        : {
            percentual: numero(percentual),
            ocorridoEm: ocorridoEm || null,
            prazoDeValidade: prazo.trim() || null,
            adquirente: saiu ? adquirente.trim() || null : null,
            vinculoEntrePartes: saiu ? vinculo.trim() || null : null,
            proprietarioAtual: saiu ? proprietarioAtual.trim() || null : null,
            /* ⭐ Só na participação no exterior: nos outros, estes campos vão como vieram. */
            ...(ehExterior
              ? { cidade: jurisdicao.trim() || null, cartorio: registro.trim() || null, qualidadeNoAto: qualidade.trim() || null, prazoDeValidade: null }
              : {}),
            /* ⭐ Só no direito creditório: o devedor do crédito, e sem vigência. */
            ...(ehCredito ? { outraParte: devedor.trim() || null, prazoDeValidade: null } : {}),
            avaliacoes: linhasDeAvaliacao.map((a) => ({
              ...(a.id ? { id: a.id } : {}),
              modalidade: a.modalidade || null,
              valor: numero(a.valor)!,
              referencia: a.referencia.trim() || null,
              avaliadaEm: a.avaliadaEm || null,
              entraNaConta: a.conta,
            })),
          }),
    };
    try {
      if (corrigindo) {
        await apiInvestigados.editarAtivo(investigado.id, corrigindo.id, dados);
        avisar('Lançamento corrigido.');
        aoGravar();
        return;
      }
      const { ativo: criado } = await apiInvestigados.acrescentarAtivo(investigado.id, dados);
      /* ⭐ Os gravames seguem junto, como no veículo — a falha de um não derruba o lançamento. */
      const aLancar = gravames.filter((g) => g.natureza.trim().length >= 2);
      let falharam = 0;
      for (const g of aLancar) {
        try {
          await apiInvestigados.acrescentarOnus(investigado.id, criado.id, {
            natureza: g.natureza.trim(),
            credor: g.credor.trim() || null,
            valor: numero(g.valor),
            constituidoEm: g.constituidoEm || null,
            situacao: g.situacao || null,
          });
        } catch {
          falharam += 1;
        }
      }
      const oQue = `${OUTROS.find((x) => x.chave === outro)!.um} lançad${outro === 'direito_minerario' || outro === 'direito_creditorio' ? 'o' : 'a'}`;
      avisar(
        aLancar.length === 0
          ? `${oQue}.`
          : falharam === 0
            ? `${oQue}, com ${aLancar.length} gravame(s).`
            : `${oQue}; ${falharam} gravame(s) falharam e precisam ser lançados à mão.`,
      );
      aoGravar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
      emVoo.current = false;
      setGravando(false);
    }
  }

  const situacoes = SITUACOES_DO_OUTRO[outro];
  const situacao = situacoes.find((s) => s.chave === classificacao);
  const marcada = linhasDeAvaliacao.find((a) => a.conta);
  const pista = PISTA_DO_OUTRO[outro];
  const ajuda = passo < ULTIMO ? (ehAcao ? AJUDA_DA_ACAO : ehExterior ? AJUDA_DO_EXTERIOR : ehCredito ? AJUDA_DO_CREDITO : AJUDA_DO_REGISTRO)[passo] : undefined;
  const ajudaEmFaixa = (!ehAcao && passo === 2) || passo === 3;
  const nomeDaFicha = OUTROS.find((x) => x.chave === outro)!.um.toLowerCase();
  const numeroDoGravame = (t: string) => (numero(t) !== null ? dinheiro(numero(t)!) : '');

  return (
    <div className="imv pap-ficha va-ficha" ref={topo}>
      <section className="imv-ficha" aria-label={`${corrigindo ? 'Corrigir' : 'Lançar'} outro ativo`}>
        <header className="imv-ficha-topo">
          <div>
            <span className="s-sobrelinha">
              {corrigindo ? 'Corrigindo' : 'Novo ativo'} · passo {passo + 1} de {passos.length}
            </span>
            <h3>
              {corrigindo ? 'Corrigir ' : 'Lançar '}
              {nomeDaFicha} de {investigado.nome}
            </h3>
            <p>Um assunto por vez. Volte a qualquer passo sem perder o que digitou.</p>
          </div>
          <button type="button" className="imv-fechar" onClick={guarda.fechar} aria-label="Fechar sem gravar">
            <Icone nome="fechar" tamanho={16} />
          </button>
        </header>
        <PassosDoTopo passos={passos} atual={passo} alcancado={alcancado} aoIr={irPara} />

        <div className="imv-corpo">
          <div className="imv-conteudo">
            {ajuda && ajudaEmFaixa && (
              <AjudaDoPasso titulo={ajuda.titulo} faixa>
                {ajuda.corpo()}
              </AjudaDoPasso>
            )}

            {/* ── O BEM ─────────────────────────────────────────────────── */}
            {passo === 0 && (
              <>
                <span className="imv-rotulo">O que é</span>
                <div className="imv-seg va-seg" role="radiogroup" aria-label="Que ativo é">
                  {OUTROS.map((o) => (
                    <button key={o.chave} type="button" role="radio" aria-checked={outro === o.chave} className={`imv-op neutro${outro === o.chave ? ' on' : ''}`} onClick={() => trocarOutro(o.chave)}>
                      <i />
                      {o.um}
                    </button>
                  ))}
                </div>
                <span className="imv-rotulo">{ehAcao ? 'Situação da posição' : 'Situação'}</span>
                <div className="imv-seg va-seg" role="radiogroup" aria-label="Situação">
                  {situacoes.map((s) => (
                    <button key={s.chave} type="button" role="radio" aria-checked={classificacao === s.chave} className={`imv-op ${s.tom}${classificacao === s.chave ? ' on' : ''}`} onClick={() => setClassificacao(s.chave)}>
                      <i />
                      {s.curto}
                    </button>
                  ))}
                </div>
                {situacao && <p className={`imv-def ${situacao.tom}`}>{situacao.define}</p>}
                <div className="imv-grade-campos va-campos">
                  <CampoDoPasso
                    rotulo={pista.rotulo}
                    obrigatorio
                    col={6}
                    nota={
                      ehExterior
                        ? 'O nome da sociedade, como o registro da jurisdição o escreve.'
                        : ehCredito
                          ? 'O que é o crédito, como o documento o descreve: precatório, crédito em processo, título ou contrato.'
                          : 'Nomeie o registro que detém o fato — a companhia, o INPI, a ANM. Nunca a plataforma consultada.'
                    }
                  >
                    <input id="oa-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder={pista.bem} autoFocus={!corrigindo} />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo={ehAcao ? 'Identificação' : ehCredito ? 'Número' : 'Registro'} col={3} mono>
                    <input id="oa-identificacao" value={identificacao} onChange={(e) => setIdentificacao(e.target.value)} placeholder={pista.registro} />
                  </CampoDoPasso>
                  {ehCredito && (
                    <CampoDoPasso rotulo="Devedor do crédito" col={3} nota="Quem deve à parte: o ente público, a empresa, a pessoa.">
                      <input id="oa-devedor" value={devedor} onChange={(e) => setDevedor(e.target.value)} placeholder="Estado de São Paulo" />
                    </CampoDoPasso>
                  )}
                  {ehExterior && (
                    <>
                      <CampoDoPasso rotulo="Constituída em" obrigatorio col={3} nota="O país ou a jurisdição. É o que o documento diz: sociedade constituída em…">
                        <input id="oa-jurisdicao" value={jurisdicao} onChange={(e) => setJurisdicao(e.target.value)} placeholder="Ilhas Virgens Britânicas" />
                      </CampoDoPasso>
                      <CampoDoPasso rotulo="Qualidade do investigado" col={3} nota="Sócio, diretor, beneficiário final. Escrita livre.">
                        <input id="oa-qualidade" list="oa-qualidades" value={qualidade} onChange={(e) => setQualidade(e.target.value)} placeholder="Sócio" />
                        <datalist id="oa-qualidades">
                          {QUALIDADES_NO_EXTERIOR.map((q) => (
                            <option key={q} value={q} />
                          ))}
                        </datalist>
                      </CampoDoPasso>
                    </>
                  )}
                  {ehAcao && (
                    <CampoDoPasso rotulo="Situação da companhia" col={3} nota="Como o cadastro da CVM escreve: o registro, a situação do emissor, o mercado.">
                      <input id="oa-companhia" value={companhia} onChange={(e) => setCompanhia(e.target.value)} placeholder="Registro ativo · fase operacional · bolsa" />
                    </CampoDoPasso>
                  )}
                </div>
              </>
            )}

            {/* ── A POSIÇÃO (ação) ────────────────────────────────────────── */}
            {passo === 1 && ehAcao && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo="Quantidade de ações" col={2} mono>
                  <input id="oa-quantidade" inputMode="numeric" value={quantidade} onChange={(e) => setQuantidade(e.target.value.replace(/[^\d.]/g, ''))} onBlur={() => setQuantidade(q === null ? '' : q.toLocaleString('pt-BR'))} placeholder="—" />
                </CampoDoPasso>
                <CampoDoPasso rotulo="Do capital" col={2} mono nota="Como o documento diz. Não multiplica o valor.">
                  <input id="oa-capital" inputMode="decimal" value={capital} onChange={(e) => setCapital(e.target.value)} placeholder="—" />
                  <i className="imv-suf">%</i>
                </CampoDoPasso>
                <CampoDoPasso rotulo={deixou ? 'Constava em' : 'Consta em'} col={2} mono nota={deixou ? 'A data do último documento em que ela aparecia.' : 'A data do documento que mostra a posição.'}>
                  <input id="oa-certidao" type="date" value={certidaoEm} onChange={(e) => setCertidaoEm(e.target.value)} />
                </CampoDoPasso>
              </div>
            )}

            {/* ── O REGISTRO (marca, patente, mineração) ──────────────────── */}
            {passo === 1 && !ehAcao && (
              <div className="imv-grade-campos">
                <CampoDoPasso rotulo={rotuloDaData(classificacao)} col={2} mono>
                  <input id="oa-data" type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
                </CampoDoPasso>
                <CampoDoPasso rotulo="Participação" col={2} mono nota="Em branco não vira 100%.">
                  <input id="oa-percentual" inputMode="decimal" value={percentual} onChange={(e) => setPercentual(e.target.value)} placeholder="—" />
                  <i className="imv-suf">%</i>
                </CampoDoPasso>
                <CampoDoPasso rotulo={ehExterior ? 'Conferida em' : 'Situação consultada em'} col={2} mono nota="De quando é o retrato.">
                  <input id="oa-certidao" type="date" value={certidaoEm} onChange={(e) => setCertidaoEm(e.target.value)} />
                </CampoDoPasso>
                {ehExterior ? (
                  <CampoDoPasso rotulo="Confirmada perante" obrigatorio col={6} nota="O registro da jurisdição que conferiu a participação. É ele que o documento cita; a base do ICIJ, nunca.">
                    <input id="oa-registro" value={registro} onChange={(e) => setRegistro(e.target.value)} placeholder="Registro de sociedades das Ilhas Virgens Britânicas" />
                  </CampoDoPasso>
                ) : ehCredito ? null : (
                  <CampoDoPasso rotulo="Vigência" col={3}>
                    <input id="oa-prazo" value={prazo} onChange={(e) => setPrazo(e.target.value)} placeholder="até 12/03/2031" />
                  </CampoDoPasso>
                )}
                {saiu && (
                  <>
                    <span className="imv-campo c3" />
                    <CampoDoPasso rotulo={classificacao === 'mineral_cedido' || classificacao === 'credito_cedido' ? 'Cedido para' : 'Transferida para'} col={3}>
                      <input id="oa-adquirente" value={adquirente} onChange={(e) => setAdquirente(e.target.value)} />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Vínculo entre as partes" col={3} nota="Só quando o documento demonstra.">
                      <input id="oa-vinculo" value={vinculo} onChange={(e) => setVinculo(e.target.value)} placeholder="Irmão do investigado" />
                    </CampoDoPasso>
                    <CampoDoPasso rotulo="Titular atual" col={3}>
                      <input id="oa-titular" value={proprietarioAtual} onChange={(e) => setProprietarioAtual(e.target.value)} />
                    </CampoDoPasso>
                  </>
                )}
              </div>
            )}

            {/* ── A COTAÇÃO (ação) ────────────────────────────────────────── */}
            {passo === 2 && ehAcao && (
              <>
                <div className="imv-grade-campos">
                  <CampoDoPasso rotulo="Cotação de fechamento" col={2} mono nota="Por ação, no pregão da data ao lado.">
                    <i className="imv-pre">R$</i>
                    <input id="oa-cotacao" inputMode="decimal" value={cotacao} onChange={(e) => setCotacao(digitarReais(e.target.value))} onBlur={(e) => setCotacao(completarReais(e.target.value))} placeholder="0,00" />
                  </CampoDoPasso>
                  <CampoDoPasso rotulo="Data da cotação" col={2} mono>
                    <input id="oa-cotacao-em" type="date" value={cotacaoEm} onChange={(e) => setCotacaoEm(e.target.value)} />
                  </CampoDoPasso>
                </div>
                <p className={`imv-def ${valorDaPosicao !== null && !deixou ? 'livre' : 'neutro'} oa-conta`}>
                  {valorDaPosicao !== null ? (
                    <>
                      Valor da posição: <b className="mono">{dinheiro(valorDaPosicao)}</b> — {q!.toLocaleString('pt-BR')} ações × {dinheiro(c!)}. O Hub grava esta conta; ela não se digita.
                      {deixou && ' Não soma: não se sabe se a ação ainda é dele.'}
                    </>
                  ) : (
                    'Sem quantidade e cotação, o valor fica em branco — valor não apurado, nunca zero.'
                  )}
                </p>
              </>
            )}

            {/* ── AVALIAÇÕES (marca, patente, mineração) ──────────────────── */}
            {passo === 2 && !ehAcao && (
              <div className="imv-bloco va-bloco-topo">
                <TabelaDeAvaliacoes avaliacoes={avaliacoes} setAvaliacoes={setAvaliacoes} />
              </div>
            )}

            {/* ── GRAVAMES ────────────────────────────────────────────────── */}
            {passo === 3 && (
              <div className="imv-bloco va-bloco-topo">
                {corrigindo ? (
                  <>
                    <div className="imv-tabela">
                      <div className="imv-tl imv-tl-cab imv-gr-grade">
                        <span>Natureza</span>
                        <span>Credor ou órgão</span>
                        <span>Valor</span>
                        <span>Desde</span>
                        <span>Situação</span>
                        <span />
                      </div>
                      {onusExistentes.length === 0 && <p className="imv-nada">Nenhum gravame lançado.</p>}
                      {onusExistentes.map((o) => (
                        <div key={o.id} className="imv-tl imv-gr-grade">
                          <span>{o.natureza}</span>
                          <span>{o.credor ?? '—'}</span>
                          <span className="mono">{o.valor !== null ? dinheiro(o.valor) : '—'}</span>
                          <span className="mono">{o.constituidoEm ? paraTela(o.constituidoEm) : '—'}</span>
                          <span className={`imv-mini-sit${o.situacao ? '' : ' na'}`}>{o.situacao ?? 'não apurada'}</span>
                          {ehMaster ? (
                            <button type="button" className="imv-x" aria-label="Apagar este gravame" onClick={() => setGravameAApagar(o)}>
                              <Icone nome="lixeira" tamanho={14} />
                            </button>
                          ) : (
                            <span />
                          )}
                        </div>
                      ))}
                    </div>
                    {ehMaster && (
                      <FormularioDeOnus
                        ativo={{ ...corrigindo, onus: onusExistentes }}
                        investigadoId={investigado.id}
                        aoLancar={() =>
                          void apiInvestigados
                            .ativos(investigado.id)
                            .then((r) => setOnusExistentes(r.ativos.find((x) => x.id === corrigindo.id)?.onus ?? onusExistentes))
                        }
                      />
                    )}
                    <p className="imv-nota">Gravame lançado ou apagado aqui grava na hora.</p>
                  </>
                ) : (
                  <>
                    <div className="imv-tabela">
                      <div className="imv-tl imv-tl-cab imv-gr-grade">
                        <span>Natureza</span>
                        <span>Credor ou órgão</span>
                        <span>Valor</span>
                        <span>Desde</span>
                        <span>Situação</span>
                        <span />
                      </div>
                      {gravames.length === 0 && <p className="imv-nada">Nenhum gravame.</p>}
                      {gravames.map((g, i) => {
                        const mudar = (parte: Partial<GravameNaFicha>) => setGravames((todos) => todos.map((x, k) => (k === i ? { ...x, ...parte } : x)));
                        return (
                          <div key={i} className="imv-tl imv-gr-grade">
                            <input aria-label="Natureza" list="oa-naturezas" value={g.natureza} onChange={(e) => mudar({ natureza: e.target.value })} placeholder={NATUREZAS[outro][0]} />
                            <input aria-label="Credor ou órgão" value={g.credor} onChange={(e) => mudar({ credor: e.target.value })} placeholder="Banco · juízo" />
                            <input aria-label="Valor" className="mono" inputMode="decimal" value={g.valor} onChange={(e) => mudar({ valor: digitarReais(e.target.value) })} onBlur={(e) => mudar({ valor: completarReais(e.target.value) })} placeholder="—" />
                            <input aria-label="Constituído em" type="date" value={g.constituidoEm} onChange={(e) => mudar({ constituidoEm: e.target.value })} />
                            <select aria-label="Situação" value={g.situacao} onChange={(e) => mudar({ situacao: e.target.value })}>
                              <option value="">não apurada</option>
                              <option value="ATIVO">ativo</option>
                              <option value="CANCELADO">cancelado</option>
                              <option value="QUITADO SEM BAIXA">quitado sem baixa</option>
                            </select>
                            <button type="button" className="imv-x" aria-label="Tirar este gravame" onClick={() => setGravames((todos) => todos.filter((_, k) => k !== i))}>
                              <Icone nome="fechar" tamanho={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="imv-mais" onClick={() => setGravames((todos) => [...todos, { natureza: '', credor: '', valor: '', constituidoEm: '', situacao: '' }])}>
                      <Icone nome="mais" tamanho={14} /> Acrescentar gravame
                    </button>
                    <datalist id="oa-naturezas">
                      {NATUREZAS[outro].map((n) => (
                        <option key={n} value={n} />
                      ))}
                    </datalist>
                  </>
                )}
              </div>
            )}

            {/* ── CONFERIR ────────────────────────────────────────────────── */}
            {passo === ULTIMO && (
              <div className="cad-resumo">
                <ResumoDoPasso
                  titulo={passos[0]!}
                  aoCorrigir={() => irPara(0)}
                  linhas={[
                    ['O que é', OUTROS.find((x) => x.chave === outro)!.um],
                    ['Situação', situacao?.curto ?? ''],
                    [pista.rotulo.replace(/^(A|O) /, '').replace(/^./, (x) => x.toUpperCase()), descricao.trim()],
                    [ehAcao ? 'Identificação' : 'Registro', identificacao.trim()],
                    ...(ehAcao ? ([['Companhia', companhia.trim()]] as Array<[string, string]>) : []),
                    ...(ehExterior
                      ? ([
                          ['Constituída em', jurisdicao.trim()],
                          ['Qualidade', qualidade.trim()],
                        ] as Array<[string, string]>)
                      : []),
                  ]}
                />
                <ResumoDoPasso
                  titulo={passos[1]!}
                  aoCorrigir={() => irPara(1)}
                  linhas={
                    ehAcao
                      ? [
                          ['Quantidade', q !== null ? `${q.toLocaleString('pt-BR')} ações` : ''],
                          ['Do capital', capital.trim() ? `${capital.trim()}%` : ''],
                          [deixou ? 'Constava em' : 'Consta em', certidaoEm ? paraTela(certidaoEm) : ''],
                        ]
                      : [
                          [rotuloDaData(classificacao), ocorridoEm ? paraTela(ocorridoEm) : ''],
                          ['Participação', percentual.trim() ? `${percentual.trim()}%` : ''],
                          ...(ehExterior
                            ? ([
                                ['Confirmada perante', registro.trim()],
                                ['Conferida em', certidaoEm ? paraTela(certidaoEm) : ''],
                              ] as Array<[string, string]>)
                            : ([
                                ['Vigência', prazo.trim()],
                                ['Consultada em', certidaoEm ? paraTela(certidaoEm) : ''],
                              ] as Array<[string, string]>)),
                          ...(saiu
                            ? ([
                                ['Para', adquirente.trim()],
                                ['Vínculo', vinculo.trim()],
                                ['Titular atual', proprietarioAtual.trim()],
                              ] as Array<[string, string]>)
                            : []),
                        ]
                  }
                />
                <ResumoDoPasso
                  titulo={passos[2]!}
                  aoCorrigir={() => irPara(2)}
                  linhas={
                    ehAcao
                      ? [
                          ['Cotação', c !== null ? `${dinheiro(c)}${cotacaoEm ? ` em ${paraTela(cotacaoEm)}` : ''}` : ''],
                          ['Valor da posição', valorDaPosicao !== null ? `${dinheiro(valorDaPosicao)}${deixou ? ' · não soma' : ''}` : ''],
                        ]
                      : linhasDeAvaliacao.length === 0
                        ? [['Avaliação', '']]
                        : linhasDeAvaliacao.map((a, i) => [
                            a.modalidade ? ROTULO_DA_MODALIDADE[a.modalidade] : `Avaliação ${i + 1}`,
                            `${numero(a.valor) !== null ? dinheiro(numero(a.valor)!) : '—'}${a === marcada ? ' · a principal' : ''} · fora da conta`,
                          ])
                  }
                />
                <ResumoDoPasso
                  titulo="Gravames"
                  aoCorrigir={() => irPara(3)}
                  linhas={
                    (corrigindo
                      ? onusExistentes.map((o) => [o.natureza, [o.credor, o.valor !== null ? dinheiro(o.valor) : null, o.situacao ?? 'não apurada'].filter(Boolean).join(' · ')])
                      : gravames
                          .filter((g) => g.natureza.trim())
                          .map((g) => [g.natureza.trim(), [g.credor.trim(), numeroDoGravame(g.valor), g.situacao || 'não apurada'].filter(Boolean).join(' · ')])) as Array<[string, string]>
                  }
                />
              </div>
            )}
          </div>
          {ajuda && !ajudaEmFaixa && <AjudaDoPasso titulo={ajuda.titulo}>{ajuda.corpo()}</AjudaDoPasso>}
        </div>

        {erro && <p className="erro imv-erro">{erro}</p>}

        <footer className="imv-ficha-pe">
          <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
            Cancelar
          </button>
          <span className="imv-esp" />
          {passo > 0 && (
            <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
              <Icone nome="voltar" tamanho={14} /> Voltar
            </button>
          )}
          <Progresso atual={passo} total={passos.length} />
          {passo < ULTIMO && (
            <button type="button" className={`imv-bt ${corrigindo ? 'fantasma' : 'primario'}`} onClick={avancar}>
              Avançar <Icone nome="avancar" tamanho={14} />
            </button>
          )}
          {(corrigindo || passo === ULTIMO) && (
            <button type="button" className="imv-bt primario" disabled={gravando} onClick={pedirParaGravar}>
              <Icone nome="check" tamanho={14} /> {gravando ? 'Gravando…' : corrigindo ? 'Gravar a correção' : 'Lançar ativo'}
            </button>
          )}
        </footer>
      </section>

      {/* Apagar sempre pergunta — a regra dele, 18/09/2026. */}
      {gravameAApagar && corrigindo && (
        <Confirmar
          titulo="Apagar este gravame?"
          descricao={`${gravameAApagar.natureza}${gravameAApagar.credor ? ` · ${gravameAApagar.credor}` : ''} sai deste bem agora, e não volta.`}
          aoFechar={() => setGravameAApagar(null)}
          aoConfirmar={() =>
            void apiInvestigados
              .apagarOnus(investigado.id, corrigindo.id, gravameAApagar.id)
              .then(() => setOnusExistentes((l) => l.filter((x) => x.id !== gravameAApagar.id)))
              .catch(avisarErro)
          }
        />
      )}
      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={corrigindo ? `o lançamento de ${investigado.nome}` : `o ativo de ${investigado.nome}`}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}
